import { useState } from 'react';
import axios from 'axios';
import { Loader2, Sparkles } from 'lucide-react';
import { API_URL } from '../../../utils/api';
import { getErrorMessage } from '../../../utils/errors';
import { cn } from '../../../lib/utils';
import { AI_LANGUAGES, EYEBROW, FOCUS_RING, PILL_INK } from '../constants';
import { PanelHeading, Segmented } from '../controls';

const aiError = (error) => {
  const status = error?.response?.status;
  if (status === 429) return "You've used today's AI wording suggestions. Please try again tomorrow.";
  if (status === 503) return 'AI wording is not available right now. Please try again later.';
  if (status === 401) return 'Please sign in again to use AI wording.';
  return getErrorMessage(error, 'We could not write wording right now. Please try again.');
};

/** Write (and design) with AI: prompt + language, three suggestions, "Use this". No chat: it only writes. */
export const AiPanel = ({ occasion, onUse, onUseStructured, fields, designFonts, targetLabel, heading = true, defaultPrompt = '', templateIds = [] }) => {
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [language, setLanguage] = useState('yi');
  const canDesign = Boolean(fields?.length && onUseStructured && designFonts?.length);
  const [designToo, setDesignToo] = useState(true); // also sizes, colours and fonts
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [structured, setStructured] = useState(null); // per-text-box answers, same order as options
  const [error, setError] = useState('');

  const write = async () => {
    const text = prompt.trim();
    if (!text) {
      setError('Tell us a little about your simcha first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/ai/wording`, {
        prompt: text.slice(0, 1000),
        language,
        ...(occasion ? { occasion } : {}),
        // the card's own text boxes, so the wording is written into them (not into one box)
        ...(fields?.length && onUseStructured ? { fields: fields.slice(0, 40) } : {}),
        // the designs the boxes came from: the server checks them for card fields (kept away from the AI)
        ...(templateIds.length ? { template_ids: templateIds.slice(0, 5) } : {}),
        ...(canDesign && designToo ? { design: true, fonts: designFonts.slice(0, 80) } : {}),
      });
      const list = Array.isArray(data?.options) ? data.options.filter((o) => typeof o === 'string' && o.trim()) : [];
      setOptions(list);
      setStructured(Array.isArray(data?.structured) && data.structured.length === list.length ? data.structured : null);
      if (!list.length) setError('No suggestions came back. Try describing your simcha differently.');
    } catch (err) {
      setError(aiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {heading && (
        <PanelHeading>
          Write with <span className="italic text-logo-gradient">AI</span>
        </PanelHeading>
      )}
      <textarea
        aria-label="Describe your simcha"
        rows={3}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="A wedding for our son Moshe, warm and traditional wording"
        className="rounded-xl px-3 py-2.5 text-[13px] resize-none gradient-border outline-none focus-visible:ring-[3px] focus-visible:ring-pink/10"
        data-testid="ai-prompt"
      />
      <Segmented label="Language" options={AI_LANGUAGES} value={language} onChange={setLanguage} />
      {canDesign ? (
        <label className="flex items-center gap-2 text-[13px] font-bold text-ink cursor-pointer">
          <input
            type="checkbox"
            checked={designToo}
            onChange={(e) => setDesignToo(e.target.checked)}
            className="h-4 w-4 accent-[#1A1A1A]"
            data-testid="ai-design-toggle"
          />
          Also design it (sizes, colours, fonts, layout)
        </label>
      ) : null}
      <button
        type="button"
        onClick={write}
        disabled={loading}
        className={cn(PILL_INK, 'h-[42px] text-sm font-extrabold')}
        data-testid="ai-write-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Sparkles className="h-4 w-4" aria-hidden="true" />}
        {loading ? (canDesign && designToo ? 'Designing…' : 'Writing…') : canDesign && designToo ? 'Write & design' : 'Write wording'}
      </button>
      <div aria-live="polite" className="flex flex-col gap-2">
        {error ? (
          <p role="alert" className="text-[13px] text-[#B0003A] bg-[#FDF1F4] rounded-xl px-3 py-2">
            {error}
          </p>
        ) : null}
        {options.length > 0 && (
          <>
            <p className={EYEBROW}>Suggestions</p>
            {options.map((text, i) => (
              <div key={i} className="rounded-xl border border-[#EEEBE4] px-3 py-2.5 flex flex-col gap-2">
                {structured?.[i]?.style ? <p className={EYEBROW}>{structured[i].style}</p> : null}
                <p dir="auto" className="font-hebrew text-sm leading-relaxed text-ink whitespace-pre-line">
                  {text}
                </p>
                <button
                  type="button"
                  onClick={() => (structured && onUseStructured ? onUseStructured(structured[i]) : onUse(text))}
                  className={cn('self-start h-7 px-3 rounded-full border border-ink bg-white text-xs font-extrabold hover:bg-cream', FOCUS_RING)}
                  data-testid={`ai-use-${i}`}
                >
                  Use this
                </button>
              </div>
            ))}
            {structured ? (
              <p className="text-[11px] text-[#6B665C]">
                “Use this” fills each of your text boxes{structured.some((o) => o.style) ? ' and applies the design' : ''}; names and address stay as they are. Ctrl+Z undoes it.
              </p>
            ) : targetLabel ? (
              <p className="text-[11px] text-[#6B665C]">“Use this” replaces: {targetLabel}</p>
            ) : null}
          </>
        )}
      </div>
      <p className="text-[11px] text-[#6B665C]">Up to 30 suggestions a day. Always check names, dates and spelling.</p>
    </div>
  );
};

export default AiPanel;
